import React, { useState, useEffect } from 'react';
import { Database, Search, ShoppingCart, Download, ShieldCheck, FileJson, BadgeCheck, BarChart2, Scale, Loader2 } from 'lucide-react';
import { VaultItem } from '../types';

const VaultInterface: React.FC = () => {
  const [items, setItems] = useState<VaultItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetch('/api/vault')
      .then(res => res.json())
      .then(data => {
        setItems(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load vault items:', err);
        setLoading(false);
      });
  }, []);

  const filtered = items.filter(item => {
    if (typeFilter !== 'all' && item.type !== typeFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return item.title.toLowerCase().includes(s) || item.language.toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8 pb-20">
      
      {/* Hero Section */}
      <div className="flex flex-col md:flex-row justify-between items-end border-b border-zinc-800 pb-8">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
             <Database className="text-blue-500" /> The Vector Vault
          </h2>
          <p className="text-zinc-400 max-w-xl">
             Access Gold-Standard African Datasets. Refined by 10,000+ localized healers, validated by Axiom Math.
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex gap-2">
            <button className="px-4 py-2 bg-zinc-800 text-zinc-300 rounded-lg text-sm font-medium border border-zinc-700 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Izwi-Guard API
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" /> Enterprise Access
            </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
            <Search className="absolute left-3 top-3 w-5 h-5 text-zinc-500" />
            <input 
                type="text" 
                placeholder="Search vectors (e.g. 'Zulu medical', 'Swahili finance')..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 rounded-xl py-3 pl-10 pr-4 text-white focus:border-blue-500 focus:outline-none"
            />
        </div>
        <select 
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 text-zinc-300 focus:outline-none"
        >
            <option value="all">All Modalities</option>
            <option value="Video-Text">Video + Text</option>
            <option value="Audio-Text">Audio + Text</option>
            <option value="Multimodal">Multimodal</option>
        </select>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-20">
          <Database className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">No Datasets Available</h3>
          <p className="text-zinc-500">Check back soon or contact us for custom datasets.</p>
        </div>
      )}

      {/* Grid */}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((item) => (
              <div key={item.id} className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 hover:border-zinc-600 transition-all group">
                  <div className="flex justify-between items-start mb-4">
                      <div className="bg-blue-500/10 text-blue-400 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border border-blue-500/20">
                          {item.type}
                      </div>
                      {item.afroBenchScore && (
                          <div className="flex items-center gap-1.5 bg-zinc-900 px-2 py-1 rounded-md border border-zinc-700">
                               <BarChart2 className="w-3 h-3 text-green-400" />
                               <span className="text-[10px] font-mono text-zinc-300">
                                  AB: <span className="text-white font-bold">{item.afroBenchScore}%</span>
                               </span>
                          </div>
                      )}
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-1">{item.title}</h3>
                  <div className="flex items-center gap-2 mb-4">
                      <span className="text-zinc-500 text-sm">{item.language} • {item.size} Verified Items</span>
                      {item.biasScore && (
                           <span className={`text-[10px] px-1.5 py-0.5 rounded border 
                              ${item.biasScore === 'A' ? 'border-green-800 text-green-500' : 'border-yellow-800 text-yellow-500'}
                           `}>
                              Bias: {item.biasScore}
                           </span>
                      )}
                  </div>
                  
                  {item.preview && (
                    <div className="bg-black/40 rounded-lg p-3 mb-6 border border-zinc-800/50">
                        <p className="text-zinc-400 text-xs italic">"{item.preview}"</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-auto">
                      <span className="text-2xl font-mono font-bold text-white">${item.price}</span>
                      <button className="p-2 bg-white text-black rounded-lg hover:bg-zinc-200 transition-colors">
                          <Download className="w-5 h-5" />
                      </button>
                  </div>
              </div>
          ))}
          
          {/* Placeholder for custom request */}
          <div className="border-2 border-dashed border-zinc-800 rounded-2xl p-6 flex flex-col items-center justify-center text-center hover:border-zinc-700 cursor-pointer transition-all">
               <FileJson className="w-12 h-12 text-zinc-600 mb-4" />
               <h3 className="text-lg font-bold text-white">Custom Dataset Request</h3>
               <p className="text-zinc-500 text-sm mt-2">Need a specific dialect or domain? Commission our Data Hunters.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VaultInterface;
